import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { MaterialModule } from '../../../shared/material.module';
import { MatChipsModule } from '@angular/material/chips';
import { Country } from '../../../models/country';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { Observable } from 'rxjs';
import { UserSectionAccess } from '../../../models/userSectionAccess';
import { companyAccessTabs } from '../../../utilities/companyAccessTabs';
import { navigationData } from '../../../utilities/navigationData';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { CompanyAccess } from '../../../models/companyAccess';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-user-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatChipsModule
  ],
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss']
})
export class UserFormComponent {
  @Input() form!: FormGroup;
  @Input() submitButtonText = 'Submit';
  @Input() isCreateMode = false;
  @Input() createdDate?: Date;
  @Input() updatedDate?: Date;
  @Output() submit = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  private lookupService = inject(LookupDataService);

  countries$: Observable<Country[] | null>;
  countriesLoading$: Observable<boolean>;
  private countries: Country[] = [];

  //countries: Country[] = Helpers.testCountries.sort((a, b) => a.name.localeCompare(b.name));
  tabs: CompanyAccess[] = companyAccessTabs;
  measurements: string[] = Helpers.measurements();
  sections = navigationData;

  selectedSections: UserSectionAccess[] = [];
  hidePassword = true;
  hideConfirmPassword = true;

  constructor(private confirmationDialogService: ConfirmationDialogService) {
    this.countries$ = this.lookupService.getVisibleCountries();
    this.countriesLoading$ = this.lookupService.getTableLoadingState('countries');
    this.countries$.subscribe(data => {
      if (data) this.countries = data.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  ngOnInit() {
    // Sync local selectedSections array with form control when form is patched (for edit mode)
    this.form.get('selectedSections')?.valueChanges.subscribe(sections => {
      if (sections && Array.isArray(sections)) {
        this.selectedSections = sections;
      }
    });

    // Initialize selectedSections from form value if already set
    const initialSections = this.form.get('selectedSections')?.value;
    if (initialSections && Array.isArray(initialSections) && initialSections.length > 0) {
      this.selectedSections = initialSections;
    }

    // Dropping a tab drops its read-only flag with it, so re-granting the tab later doesn't
    // silently bring back a read-only mark the admin can no longer see.
    this.form.get('selectedTabs')?.valueChanges.subscribe((tabs: CompanyAccess[]) => {
      const selectedNames = (Array.isArray(tabs) ? tabs : []).map(tab => tab.name);
      const readonlyTabs = this.getReadonlyTabNames();
      const pruned = readonlyTabs.filter(name => selectedNames.includes(name));

      if (pruned.length !== readonlyTabs.length) {
        this.form.get('readonlyTabs')?.setValue(pruned);
      }
    });
  }

  onSubmit() {
    if (this.form.valid) {
      const dialogMethod = this.isCreateMode
        ? this.confirmationDialogService.openSaveConfirmationDialog('Are you sure you want to create this user?')
        : this.confirmationDialogService.openUpdateConfirmationDialog();

      dialogMethod.subscribe(result => {
        if (result) {
          this.submit.emit();
        }
      });
    }
  }

  onCancel() {
    this.cancel.emit();
  }

  getCountryName(countryId: string): string {
    return this.countries.find(c => c.id === countryId)?.name || '';
  }

  selectAllCountries() {
    const allCountryIds = this.countries.map(c => c.id);
    this.form.get('selectedCountries')?.setValue(allCountryIds);
  }

  deselectAllCountries() {
    this.form.get('selectedCountries')?.setValue([]);
  }

  getTabName(tab: CompanyAccess): string {
    return tab.name;
  }

  selectAllTabs() {
    this.form.get('selectedTabs')?.setValue([...this.tabs]);
  }

  deselectAllTabs() {
    this.form.get('selectedTabs')?.setValue([]);
    this.form.get('readonlyTabs')?.setValue([]);
  }

  getMeasurementName(measurement: string): string {
    return measurement;
  }

  selectAllMeasurements() {
    this.form.get('selectedMeasurements')?.setValue([...this.measurements]);
  }

  deselectAllMeasurements() {
    this.form.get('selectedMeasurements')?.setValue([]);
  }

  getTabChipClass(tab: CompanyAccess): string {
    return this.isTabReadonly(tab) ? 'chip-tab-item' : 'chip-basic-item chip-access-item';
  }

  isTabReadonly(tab: CompanyAccess): boolean {
    return this.getReadonlyTabNames().includes(tab.name);
  }

  private getReadonlyTabNames(): string[] {
    const value = this.form.get('readonlyTabs')?.value;
    return Array.isArray(value) ? value : [];
  }

  isSectionSelected(section: string, subSection: string): boolean {
    return this.selectedSections.find(s => s.section === section && s.subSection === subSection) !== undefined;
  }

  toggleSection(section: string, subSection: string) {
    if (this.selectedSections.find(s => s.section === section && s.subSection === subSection)) {
      this.selectedSections = this.selectedSections.filter(s => s.section !== section || s.subSection !== subSection);
    } else {
      this.selectedSections.push({
        section: section,
        subSection: subSection,
        visible: true,
        canCreate: true,
        canEdit: true,
        canDelete: true
      });
    }
    this.form.get('selectedSections')?.setValue(this.selectedSections);
  }

  addSection(section: string, subSection: string) {
    var selectedSection = this.selectedSections.find(s => s.section === section && s.subSection === subSection);
    if (selectedSection) {
      if (selectedSection.canCreate) {
        selectedSection.canCreate = false;
      } else {
        selectedSection.canCreate = true;
      }
    } else {
      this.selectedSections.push({
        section: section,
        subSection: subSection,
        visible: true,
        canCreate: true,
        canEdit: false,
        canDelete: false
      });
    }
    this.form.get('selectedSections')?.setValue(this.selectedSections);
  }

  editSection(section: string, subSection:  string) {
    var selectedSection = this.selectedSections.find(s => s.section === section && s.subSection === subSection);
    if (selectedSection) {
      if (selectedSection.canEdit) {
        selectedSection.canEdit = false;
      } else {
        selectedSection.canEdit = true;
      }
    } else {
      this.selectedSections.push({
        section: section,
        subSection: subSection,
        visible: true,
        canCreate: false,
        canEdit: true,
        canDelete: false
      });
    }
    this.form.get('selectedSections')?.setValue(this.selectedSections);
  }

  deleteSection(section: string, subSection: string) {
    var selectedSection = this.selectedSections.find(s => s.section === section && s.subSection === subSection);
    if (selectedSection) {
      if (selectedSection.canDelete) {
        selectedSection.canDelete = false;
      } else {
        selectedSection.canDelete = true;
      }
    } else {
      this.selectedSections.push({
        section: section,
        subSection: subSection,
        visible: true,
        canCreate: false,
        canEdit: false,
        canDelete: true
      });
    }
    this.form.get('selectedSections')?.setValue(this.selectedSections);
  }

  getAddSectionIconClass(section: string, subSection: string): string {
    var selectedSection = this.selectedSections.find(s => s.section === section && s.subSection === subSection);

    if (selectedSection?.canCreate) {
      return 'section-panel-item-icon-selected';
    } else {
      return 'section-panel-item-icon';
    }
  }

  getEditSectionIconClass(section: string, subSection: string): string {
    var selectedSection = this.selectedSections.find(s => s.section === section && s.subSection === subSection);

    if (selectedSection?.canEdit) {
      return 'section-panel-item-icon-selected';
    } else {
      return 'section-panel-item-icon';
    }
  }

  getDeleteSectionIconClass(section: string, subSection: string): string {
    var selectedSection = this.selectedSections.find(s => s.section === section && s.subSection === subSection);

    if (selectedSection?.canDelete) {
      return 'section-panel-item-icon-selected';
    } else {
      return 'section-panel-item-icon';
    }
  }

  getNumberOfSelectedSubsections(section: { displayName: string; children?: { displayName: string }[] }): string {
    const total = section.children?.length ?? 0;
    const checked = this.selectedSections.filter(s => s.section === section.displayName).length;
    return total > 0 ? `(${checked}/${total})` : '';
  }

  /** Clicking a chip flips that tab between full access and read-only. */
  toggleTab(tab: CompanyAccess) {
    const readonlyTabs = this.getReadonlyTabNames();
    const next = readonlyTabs.includes(tab.name)
      ? readonlyTabs.filter(name => name !== tab.name)
      : [...readonlyTabs, tab.name];

    const control = this.form.get('readonlyTabs');
    control?.setValue(next);
    control?.markAsDirty();
  }

  getSelectedMeasurementsArray(): any[] {
    const value = this.form.get('selectedMeasurements')?.value;
    return Array.isArray(value) ? value : [];
  }

  getSelectedCountriesArray(): any[] {
    const value = this.form.get('selectedCountries')?.value;
    return Array.isArray(value) ? value : [];
  }

  getSelectedTabsArray(): any[] {
    const value = this.form.get('selectedTabs')?.value;
    return Array.isArray(value) ? value : [];
  }

  compareTabsByName(tab1: CompanyAccess, tab2: CompanyAccess): boolean {
    return tab1 && tab2 ? tab1.name === tab2.name : tab1 === tab2;
  }

  compareCountriesById(country1: string, country2: string): boolean {
    return country1 === country2;
  }

  compareMeasurements(measurement1: string, measurement2: string): boolean {
    return measurement1 === measurement2;
  }

  selectAllSections() {
    const allSections: UserSectionAccess[] = [];
    this.sections.forEach(section => {
      if (section.children) {
        section.children.forEach(child => {
          allSections.push({
            section: section.displayName,
            subSection: child.displayName,
            visible: true,
            canCreate: true,
            canEdit: true,
            canDelete: true
          });
        });
      }
    });
    this.selectedSections = allSections;
    this.form.get('selectedSections')?.setValue(this.selectedSections);
  }

  deselectAllSections() {
    this.selectedSections = [];
    this.form.get('selectedSections')?.setValue([]);
  }
}
